import gsap from "gsap"

export class Marquee {
  constructor() {
    this.isInitialized = false;
    this.resizeHandler = null;
    this.marquees = new Map();
    this.prefersReducedMotion = false;
    this.isMobile = false;
  }

  init() {
    if (this.isInitialized) return;

    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    this.resizeHandler = this.debounceOnWidthChange(() => {
      this.isMobile = window.innerWidth <= 768;
      this.initMarquees();
    }, 250);

    this.initMarquees();
    window.addEventListener('resize', this.resizeHandler);

    this.isInitialized = true;
  }

  initMarquees() {
    document.querySelectorAll('[data-marquee]').forEach((container, index) => {

      if (this.marquees.has(container)) {
        this.destroyMarquee(container);
      }

      const initDelay = this.isMobile ? 100 : 0;

      setTimeout(() => {
        const marquee = new MarqueeInstance(container, index, {
          prefersReducedMotion: this.prefersReducedMotion,
          isMobile: this.isMobile
        });
        this.marquees.set(container, marquee);
      }, initDelay);
    });
  }

  destroyMarquee(container) {
    const marquee = this.marquees.get(container);
    if (marquee) {
      marquee.destroy();
      this.marquees.delete(container);
    }
  }

  debounceOnWidthChange(fn, ms) {
    let last = innerWidth, timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (innerWidth !== last) {
          last = innerWidth;
          fn.apply(this, args);
        }
      }, ms);
    };
  }

  destroy() {
    this.marquees.forEach((marquee, container) => {
      this.destroyMarquee(container);
    });

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    this.isInitialized = false;
  }
}

class MarqueeInstance {
  constructor(container, index, options = {}) {
    this.container = container;
    this.list = container.querySelector('[data-marquee-list]');
    this.index = index;
    this.options = options;

    this.timeline = null;
    this.originalItems = [];

    this.init();
  }

  init() {
    if (this.options.prefersReducedMotion) return;

    this.setupMarquee();
  }

  setupMarquee() {
    if (!this.list) return;

    this.container.offsetHeight;

    this.originalItems = Array.from(this.list.querySelectorAll('[data-marquee-item]'));

    if (this.originalItems.length === 0) return;

    this.createSeamlessLoop();
  }

  createSeamlessLoop() {
    const allItems = Array.from(this.list.querySelectorAll('[data-marquee-item]'));
    allItems.forEach(item => {
      if (item.getAttribute('data-marquee-duplicate') === 'true') {
        item.remove();
      }
    });

    const containerWidth = this.container.getBoundingClientRect().width;
    let totalOriginalWidth = 0;

    this.originalItems.forEach(item => {
      const rect = item.getBoundingClientRect();
      const styles = getComputedStyle(item);
      const marginLeft = parseFloat(styles.marginLeft) || 0;
      const marginRight = parseFloat(styles.marginRight) || 0;
      totalOriginalWidth += rect.width + marginLeft + marginRight;
    });

    const minWidth = this.options.isMobile ? containerWidth * 4 : containerWidth * 3;
    const duplicationsNeeded = Math.max(1, Math.ceil(minWidth / totalOriginalWidth));

    for (let i = 0; i < duplicationsNeeded; i++) {
      this.originalItems.forEach(originalItem => {
        const clone = originalItem.cloneNode(true);
        clone.setAttribute('data-marquee-duplicate', 'true');
        this.list.appendChild(clone);
      });
    }

    const allItemsAfterDupe = Array.from(this.list.querySelectorAll('[data-marquee-item]'));
    this.createSimpleAnimation(allItemsAfterDupe, totalOriginalWidth);
  }

  createSimpleAnimation(items, originalWidth) {
    const customSpeed = parseFloat(this.container.getAttribute('data-marquee-speed'));

    let speedMultiplier = customSpeed || 1;

    if (this.options.isMobile) {
      speedMultiplier *= 0.5;
    }

    const duration = (originalWidth / 100) / speedMultiplier;

    if (this.timeline) {
      this.timeline.kill();
    }

    gsap.set(this.list, { x: 0 });

    this.timeline = gsap.to(this.list, {
      x: -originalWidth,
      duration: duration,
      ease: "none",
      repeat: -1,
      onRepeat: () => {
        gsap.set(this.list, { x: 0 });
      }
    });
  }

  destroy() {
    if (this.timeline) {
      this.timeline.kill();
      this.timeline = null;
    }

    if (this.list) {
      gsap.set(this.list, { x: 0 });

      const allItems = Array.from(this.list.querySelectorAll('[data-marquee-item]'));
      allItems.forEach(item => {
        if (item.getAttribute('data-marquee-duplicate') === 'true') {
          item.remove();
        }
      });
    }

    this.originalItems = [];
  }
}